import { useContext, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import useFetch from "../../../../hooks/useFetch";
import { useGetData } from "../../../../hooks/useGetData";
import { EstadoInterface } from "../../../../interfaces/EstadoInterface";
import { AuthContext } from "../../../../context/AuthContext";
import ForbiddenMessage from "../../../Mensajes/ForbiddenMessage";
import SuccessMessage from "../../../Mensajes/SuccessMessage";
import ServerMessage from "../../../Mensajes/ServerMessage";
import { toast } from "react-toastify";

/**
 * SucursalCrear Component
 * @description: Componente que permite agregar una nueva sucursal.
 * @date [fecha actual]
 * @param Props Ninguno
 * @returns JSX del formulario de creación de sucursal.
 */

const SucursalCrear = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const { sendRequest, error, setError, serverMessage, setServerMessage } =
    useFetch();
  const { data: estados } = useGetData("/estado", "estado");
  const [estado, setEstado] = useState<number>();
  const [correos, setCorreos] = useState<string[]>([""]);

  //Formulario
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      nombre: "",
      domicilio: "",
      latitud: "",
      longitud: "",
      id_nmp: "",
      correo: "",
      estatus: true,
      estado_id: "",
    },
  });

  //Función que hace el POST para crear la sucursal.
  const createSucursal = (data: any) => {
    if (authContext.plataformas.length === 0) {
      toast.error("No tienes plataformas asignadas", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      return;
    }

    setError(null);
    setServerMessage(undefined);
    const body = {
      nombre: data.nombre,
      domicilio: data.domicilio,
      latitud: parseFloat(data.latitud),
      longitud: parseFloat(data.longitud),
      estado_id: estado,
      estatus: data.estatus,
      id_nmp: data.id_nmp || null,
      correo: correos.filter(c => c.trim() !== "").join(",") || null,
      plataforma_id: authContext.plataformas[0]?.id, // Asignar plataforma activa
    };

    sendRequest(
      {
        url: "/sucursal",
        method: "POST",
        body: body,
      },
      () => {
        setShow(true);
        reset();
        setEstado(undefined);
        setCorreos([""]);
      }
    );
  };

  //Si no tiene permisos para crear sucursales
  if (error === 403) {
    return <ForbiddenMessage onCloseHandler={() => setError(null)} />;
  }

  return (
    <Container style={{ marginTop: "20px" }}>
      <h2>Crear nueva sucursal</h2>
      <Form onSubmit={handleSubmit(createSucursal)}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre de la sucursal *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese el nombre de la sucursal"
                {...register("nombre", {
                  required: "El nombre es obligatorio",
                  minLength: {
                    value: 3,
                    message: "El nombre debe tener al menos 3 caracteres",
                  },
                })}
                isInvalid={!!errors.nombre}
              />
              <Form.Control.Feedback type="invalid">
                {errors.nombre?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>ID de Sucursal</Form.Label>
              <Form.Control
                type="number"
                placeholder="Ingrese el ID de la sucursal (opcional)"
                {...register("id_nmp", {
                  maxLength: {
                    value: 10,
                    message: "El ID no puede tener más de 10 dígitos",
                  },
                  pattern: {
                    value: /^[0-9]*$/,
                    message: "Solo se permiten números",
                  },
                })}
                isInvalid={!!errors.id_nmp}
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.id_nmp?.message}
              </Form.Control.Feedback>
            </Form.Group>

            {authContext.plataformas[0]?.id === 2 && (
              <Form.Group className="mb-3">
                <Form.Label>Correos para envío de orden de servicio</Form.Label>
                {correos.map((correo, index) => (
                  <div key={index} className="d-flex align-items-center mb-2">
                    <Form.Control
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={correo}
                      onChange={(e) => {
                        const nuevosCorreos = [...correos];
                        nuevosCorreos[index] = e.target.value;
                        setCorreos(nuevosCorreos);
                      }}
                      isInvalid={correo !== "" && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(correo)}
                    />
                    {correos.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                          if (window.confirm(`¿Eliminar el correo "${correo}"?`)) {
                            setCorreos(correos.filter((_, i) => i !== index));
                          }
                        }}
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setCorreos([...correos, ""])}
                >
                  + Agregar correo
                </Button>
                <Form.Text className="text-muted d-block mt-1">
                  La orden de servicio se enviará automáticamente a estos correos al finalizar.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Domicilio *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Ingrese el domicilio completo"
                {...register("domicilio", {
                  required: "El domicilio es obligatorio",
                  minLength: {
                    value: 10,
                    message: "El domicilio debe tener al menos 10 caracteres",
                  },
                })}
                isInvalid={!!errors.domicilio}
              />
              <Form.Control.Feedback type="invalid">
                {errors.domicilio?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Estado *</Form.Label>
              <Controller
                name="estado_id"
                control={control}
                rules={{ required: "Debe seleccionar un estado" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={estados?.map((estado: EstadoInterface) => ({
                      value: estado.id,
                      label: estado.nombre,
                    }))}
                    placeholder="Seleccione un estado"
                    onChange={(selectedOption: any) => {
                      setEstado(selectedOption?.value);
                      field.onChange(selectedOption?.value);
                    }}
                    value={
                      estado
                        ? {
                            value: estado,
                            label: estados?.find((e: EstadoInterface) => e.id === estado)?.nombre,
                          }
                        : null
                    }
                  />
                )}
              />
              {errors.estado_id && (
                <Form.Text className="text-danger">
                  {errors.estado_id.message}
                </Form.Text>
              )}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Latitud *</Form.Label>
              <Form.Control
                type="number"
                step="any"
                placeholder="Ej: 16.754888"
                {...register("latitud", {
                  required: "La latitud es obligatoria",
                  min: {
                    value: -90,
                    message: "La latitud debe estar entre -90 y 90",
                  },
                  max: {
                    value: 90,
                    message: "La latitud debe estar entre -90 y 90",
                  },
                })}
                isInvalid={!!errors.latitud}
              />
              <Form.Control.Feedback type="invalid">
                {errors.latitud?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Longitud *</Form.Label>
              <Form.Control
                type="number"
                step="any"
                placeholder="Ej: -93.124747"
                {...register("longitud", {
                  required: "La longitud es obligatoria",
                  min: {
                    value: -180,
                    message: "La longitud debe estar entre -180 y 180",
                  },
                  max: {
                    value: 180,
                    message: "La longitud debe estar entre -180 y 180",
                  },
                })}
                isInvalid={!!errors.longitud}
              />
              <Form.Control.Feedback type="invalid">
                {errors.longitud?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Estado de la sucursal</Form.Label>
              <div className="d-flex justify-content-center">
                <Form.Check
                  type="switch"
                  id="estatus-switch"
                  label="Sucursal activa"
                  {...register("estatus")}
                />
              </div>
            </Form.Group>

            <div className="mt-4">
              <small className="text-muted">
                <strong>Ayuda:</strong> Puedes obtener las coordenadas desde Google Maps:
                <br />
                1. Busca la ubicación en Google Maps
                <br />
                2. Haz clic derecho en el punto exacto
                <br />
                3. Copia las coordenadas que aparecen
              </small>
            </div>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <Button
              type="button"
              variant="danger"
              className="me-2"
              onClick={() => navigate("/main/catalogos/sucursales")}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="me-2"
              onClick={() => {
                reset();
                setEstado(undefined);
                setError(null);
                setServerMessage(undefined);
              }}
            >
              Limpiar
            </Button>
            <Button type="submit" variant="success" className="me-2">
              Crear Sucursal
            </Button>
          </Col>
        </Row>
      </Form>

      {/* Mensajes de respuesta */}
      {show && (
        <SuccessMessage 
          header="¡Éxito!" 
          body="Sucursal creada correctamente" 
          onCloseHandler={() => setShow(false)}
        />
      )}
      {serverMessage && (
        <ServerMessage 
          message={serverMessage} 
          onCloseHandler={() => setServerMessage(undefined)}
        />
      )}
    </Container>
  );
};

export default SucursalCrear;
